import React, { createContext, useContext, useState, useEffect } from 'react';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  // Initialize state with defaults (will be loaded from backend)
  const [analytics, setAnalytics] = useState({
    totalAnalyses: 0,
    emotionCounts: {
      joy: 0,
      sad: 0,
      anger: 0,
      fear: 0,
      surprise: 0,
      disgust: 0,
      neutral: 0
    },
    featureUsage: {
      text: 0,
      image: 0,
      multiPerson: 0,
      voice: 0,
      visualization3D: 0
    },
    recentAnalyses: [],
    hourlyData: Array(24).fill(0),
    lastAnalysisTime: null,
    sessionStartTime: Date.now()
  });

  // Normalize emotion names from backend to analytics keys
  const normalizeEmotion = (emotion) => {
    const emotionMap = {
      // Joy family
      'happy': 'joy',
      'happiness': 'joy',
      'joy': 'joy',
      'excitement': 'joy',
      'amazement': 'joy',
      'love': 'joy',
      'affection': 'joy',
      'pride': 'joy',
      'confidence': 'joy',
      
      // Sadness family
      'sad': 'sad',
      'sadness': 'sad',
      'sorrow': 'sad',
      'grief': 'sad',
      'shame': 'sad',
      'embarrassment': 'sad',
      
      // Anger family
      'angry': 'anger',
      'anger': 'anger',
      'rage': 'anger',
      'frustration': 'anger',
      'frustrated': 'anger',
      
      // Fear family
      'fear': 'fear',
      'anxiety': 'fear',
      'worry': 'fear',
      
      // Surprise family
      'surprised': 'surprise',
      'surprise': 'surprise',
      'shock': 'surprise',
      'bewilderment': 'surprise',
      
      // Disgust family
      'disgust': 'disgust',
      'contempt': 'disgust',
      
      // Neutral family
      'neutral': 'neutral',
      'calm': 'neutral',
      'peaceful': 'neutral',
      'confusion': 'neutral'
    };
    
    const key = emotion.toLowerCase().trim();
    return emotionMap[key] || 'neutral';
  };

  // Track emotion analysis (now sends to backend for global tracking)
  const trackAnalysis = async (type, emotion, confidence) => {
    console.log(`📊 AnalyticsContext.trackAnalysis called:`, { type, emotion, confidence });
    
    // Send to backend for global tracking
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const userId = localStorage.getItem('emotion_ai_user_id') || 'anonymous';
      const username = localStorage.getItem('emotion_ai_username') || null;
      
      await fetch(`${BACKEND_URL}/api/track-analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          emotion,
          user_id: userId,
          username
        })
      });
      
      console.log(`✅ Analytics sent to backend for global tracking`);
      
      // Refresh global analytics
      await refreshGlobalAnalytics();
    } catch (error) {
      console.error('❌ Failed to track analytics on backend:', error);
    }
    
    // Also update local state for immediate feedback
    setAnalytics(prev => {
      const emotionKey = normalizeEmotion(emotion);
      console.log(`📊 Normalized emotion: ${emotion} → ${emotionKey}`);
      console.log(`📊 Previous total analyses: ${prev.totalAnalyses}`);
      const currentHour = new Date().getHours();
      
      const newRecentAnalysis = {
        id: Date.now(),
        type,
        emotion,
        confidence,
        timestamp: new Date().toISOString(),
        timeAgo: 'Just now'
      };

      // Update hourly data
      const newHourlyData = [...prev.hourlyData];
      newHourlyData[currentHour] = (newHourlyData[currentHour] || 0) + 1;

      const newAnalytics = {
        ...prev,
        totalAnalyses: prev.totalAnalyses + 1,
        emotionCounts: {
          ...prev.emotionCounts,
          [emotionKey]: (prev.emotionCounts[emotionKey] || 0) + 1
        },
        featureUsage: {
          ...prev.featureUsage,
          [type]: (prev.featureUsage[type] || 0) + 1
        },
        recentAnalyses: [newRecentAnalysis, ...prev.recentAnalyses.slice(0, 9)],
        hourlyData: newHourlyData,
        lastAnalysisTime: Date.now()
      };
      
      console.log(`✅ New total analyses: ${newAnalytics.totalAnalyses}`);
      console.log(`✅ Feature usage ${type}: ${newAnalytics.featureUsage[type]}`);
      
      return newAnalytics;
    });
  };
  
  // Refresh global analytics from backend
  const refreshGlobalAnalytics = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/global-analytics`);
      const data = await response.json();
      
      if (data.success) {
        setAnalytics(prev => ({
          ...prev,
          totalAnalyses: data.total_analyses || 0,
          emotionCounts: {
            joy: data.emotion_counts.joy || data.emotion_counts.happiness || data.emotion_counts.excitement || 0,
            sad: data.emotion_counts.sad || data.emotion_counts.sadness || 0,
            anger: data.emotion_counts.anger || data.emotion_counts.angry || 0,
            fear: data.emotion_counts.fear || data.emotion_counts.anxiety || 0,
            surprise: data.emotion_counts.surprise || data.emotion_counts.surprised || 0,
            disgust: data.emotion_counts.disgust || 0,
            neutral: data.emotion_counts.neutral || 0
          },
          featureUsage: {
            text: data.feature_usage.text || 0,
            image: data.feature_usage.image || 0,
            multiPerson: data.feature_usage.multiPerson || 0,
            voice: data.feature_usage.voice || 0,
            visualization3D: prev.featureUsage.visualization3D || 0
          },
          hourlyData: data.hourly_data || Array(24).fill(0)
        }));
        console.log('✅ Global analytics refreshed from backend');
      }
    } catch (error) {
      console.error('❌ Failed to refresh global analytics:', error);
    }
  };

  // Track multiple emotions (for multi-person analysis)
  // Counts as 1 analysis, but tracks all detected emotions
  const trackMultipleEmotions = (emotions) => {
    if (emotions.length === 0) return;
    
    setAnalytics(prev => {
      const currentHour = new Date().getHours();
      const newHourlyData = [...prev.hourlyData];
      newHourlyData[currentHour] = (newHourlyData[currentHour] || 0) + 1;
      
      // Track all emotions but only increment total analyses once
      const updatedEmotionCounts = { ...prev.emotionCounts };
      emotions.forEach(({ emotion }) => {
        const emotionKey = normalizeEmotion(emotion);
        updatedEmotionCounts[emotionKey] = (updatedEmotionCounts[emotionKey] || 0) + 1;
      });
      
      // Create one analysis entry for the multi-person detection
      const emotionList = emotions.map(e => e.emotion).join(', ');
      const newRecentAnalysis = {
        id: Date.now(),
        type: 'multiPerson',
        emotion: `${emotions.length} people: ${emotionList}`,
        confidence: Math.round(emotions.reduce((sum, e) => sum + e.confidence, 0) / emotions.length),
        timestamp: new Date().toISOString(),
        timeAgo: 'Just now'
      };
      
      return {
        ...prev,
        totalAnalyses: prev.totalAnalyses + 1, // Only increment once
        emotionCounts: updatedEmotionCounts,
        featureUsage: {
          ...prev.featureUsage,
          multiPerson: (prev.featureUsage.multiPerson || 0) + 1
        },
        recentAnalyses: [newRecentAnalysis, ...prev.recentAnalyses.slice(0, 9)],
        hourlyData: newHourlyData,
        lastAnalysisTime: Date.now()
      };
    });
  };

  // Track 3D visualization
  const track3DVisualization = () => {
    setAnalytics(prev => ({
      ...prev,
      featureUsage: {
        ...prev.featureUsage,
        visualization3D: (prev.featureUsage.visualization3D || 0) + 1
      }
    }));
  };

  // Get top emotion
  const getTopEmotion = () => {
    const entries = Object.entries(analytics.emotionCounts);
    if (entries.length === 0 || entries.every(([, count]) => count === 0)) {
      return { emotion: 'neutral', count: 0, percentage: 0 };
    }
    const sorted = entries.sort(([, a], [, b]) => b - a);
    const [emotion, count] = sorted[0];
    const total = Object.values(analytics.emotionCounts).reduce((sum, c) => sum + c, 0);
    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
    return { emotion, count, percentage };
  };

  // Get emotion distribution
  const getEmotionDistribution = () => {
    const total = Object.values(analytics.emotionCounts).reduce((sum, count) => sum + count, 0);
    if (total === 0) {
      return [
        { emotion: 'Joy', percent: 0, color: 'bg-green-400' },
        { emotion: 'Neutral', percent: 0, color: 'bg-gray-400' },
        { emotion: 'Surprise', percent: 0, color: 'bg-yellow-400' },
        { emotion: 'Sad', percent: 0, color: 'bg-blue-400' },
        { emotion: 'Fear', percent: 0, color: 'bg-purple-400' },
        { emotion: 'Anger', percent: 0, color: 'bg-red-400' },
        { emotion: 'Disgust', percent: 0, color: 'bg-orange-400' }
      ];
    }

    return [
      { emotion: 'Joy', percent: Math.round((analytics.emotionCounts.joy / total) * 100), color: 'bg-green-400' },
      { emotion: 'Neutral', percent: Math.round((analytics.emotionCounts.neutral / total) * 100), color: 'bg-gray-400' },
      { emotion: 'Surprise', percent: Math.round((analytics.emotionCounts.surprise / total) * 100), color: 'bg-yellow-400' },
      { emotion: 'Sad', percent: Math.round((analytics.emotionCounts.sad / total) * 100), color: 'bg-blue-400' },
      { emotion: 'Fear', percent: Math.round((analytics.emotionCounts.fear / total) * 100), color: 'bg-purple-400' },
      { emotion: 'Anger', percent: Math.round((analytics.emotionCounts.anger / total) * 100), color: 'bg-red-400' },
      { emotion: 'Disgust', percent: Math.round((analytics.emotionCounts.disgust / total) * 100), color: 'bg-orange-400' }
    ].sort((a, b) => b.percent - a.percent);
  };

  // Get peak usage hour
  const getPeakHour = () => {
    const maxValue = Math.max(...analytics.hourlyData);
    if (maxValue === 0) return '12:00 PM';
    const peakIndex = analytics.hourlyData.indexOf(maxValue);
    const hour = peakIndex % 12 || 12;
    const period = peakIndex < 12 ? 'AM' : 'PM';
    return `${hour}:00 ${period}`;
  };

  // Get analyses in last hour
  const getLastHourAnalyses = () => {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    return analytics.recentAnalyses.filter(a => {
      const timestamp = new Date(a.timestamp).getTime();
      return timestamp > oneHourAgo;
    }).length;
  };

  // Reset analytics
  const resetAnalytics = () => {
    setAnalytics({
      totalAnalyses: 0,
      emotionCounts: {
        joy: 0,
        sad: 0,
        anger: 0,
        fear: 0,
        surprise: 0,
        disgust: 0,
        neutral: 0
      },
      featureUsage: {
        text: 0,
        image: 0,
        multiPerson: 0,
        voice: 0,
        visualization3D: 0
      },
      recentAnalyses: [],
      hourlyData: Array(24).fill(0),
      lastAnalysisTime: null,
      sessionStartTime: Date.now()
    });
    localStorage.removeItem('emotionAnalytics');
  };

  // Load global analytics on mount
  useEffect(() => {
    // Clear old localStorage data
    localStorage.removeItem('emotion_ai_analytics');
    console.log('🧹 Cleared old analytics from localStorage');
    
    refreshGlobalAnalytics();
  }, []);
  
  // Periodically refresh global analytics (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshGlobalAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Update time ago for recent analyses
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({
        ...prev,
        recentAnalyses: prev.recentAnalyses.map(analysis => {
          const seconds = Math.floor((Date.now() - new Date(analysis.timestamp).getTime()) / 1000);
          let timeAgo;
          if (seconds < 60) {
            timeAgo = `${seconds} sec ago`;
          } else if (seconds < 3600) {
            timeAgo = `${Math.floor(seconds / 60)} min ago`;
          } else if (seconds < 86400) { // Less than 24 hours
            timeAgo = `${Math.floor(seconds / 3600)} hr ago`;
          } else {
            const days = Math.floor(seconds / 86400);
            timeAgo = `${days} ${days === 1 ? 'day' : 'days'} ago`;
          }
          return { ...analysis, timeAgo };
        })
      }));
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const value = {
    analytics,
    trackAnalysis,
    trackMultipleEmotions,
    track3DVisualization,
    getTopEmotion,
    getEmotionDistribution,
    getPeakHour,
    getLastHourAnalyses,
    resetAnalytics,
    refreshGlobalAnalytics
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
